import React, { useState, useEffect, useCallback } from 'react';
import { Grid, TextField } from '@material-ui/core';
import ActionButtons from './ActionButtons';
import { verifyDuplicateTaskName } from '../../services/validation/validation.service';
import EditButton from './EditButton.component';
import AddSessionItemButton from './NewItemButton.component';
import { createExamTask, modifyExamTask } from '../../services/exams/sessions/sessions.service';
import { DetailsProps } from './DetailsProps.types';
import { BreadcrumbLevel } from '../DialogBreadcrumbs/DialogBreadcrumbs.types';
import useExamSessionValidation from '../../hooks/useExamSessionValidation';
import { ITaskDetails, IIdResult } from '../../models/session.types';
import { WorkMode, NewTreeItemId } from '../SessionTree/SessionTree.types';
import { useLayoutContext } from '../../Context/Layout.context';
import { AxiosResponse } from 'axios';
import { useStyles as sessionDetails } from './SessionDetails.styles';
import DeleteButton from './DeleteButton.component';
import { blockIconForInput } from './BlockItemForInput';

interface TaskDetailsProps extends DetailsProps {
  data: ITaskDetails;
  isReadOnly: () => boolean;
  onDelete: () => void;
}

const TaskDetails: React.FC<TaskDetailsProps> = (props) => {
  const sessionDetailsClasses = sessionDetails();
  const [taskNumber, setTaskNumber] = useState<string>(props.data.taskNumber);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [isFieldEdited, setIsFieldEdited] = useState<boolean>(false);
  const { openAlert } = useLayoutContext();

  const { isValidValue: isValidTaskNumber } = useExamSessionValidation({
    isEdited: isFieldEdited,
    isAdd: props.workMode === WorkMode.ADD,
    inputValue: taskNumber,
    data: props.data.taskNumber,
    verificationApi: useCallback(
      (value: string) => verifyDuplicateTaskName(props.data.examSheetId, value),
      [props.data.examSheetId],
    ),
    onBlur: true,
  });

  const saveChanges = async () => {
    if (props.data.id !== NewTreeItemId.Task) {
      await modifyExamTask(props.data.id, taskNumber).then(
        (result: AxiosResponse<IIdResult>) => {
          openAlert('success');
          props.setWorkMode(WorkMode.VIEW);
          props.onSave(result.data, false);
        },
        () => {
          openAlert('error');
        },
      );
    } else {
      await createExamTask(props.data.parentId, taskNumber).then(
        (result: AxiosResponse<IIdResult>) => {
          openAlert('success');
          props.setWorkMode(WorkMode.VIEW);
          props.onSave(result.data, true);
        },
        () => {
          openAlert('error');
        },
      );
    }
  };

  useEffect(() => {
    if (isValidTaskNumber && taskNumber !== props.data.taskNumber) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  });

  const revert = () => {
    setTaskNumber(props.data.taskNumber);
  };

  const closeHandle = () => {
    props.onClose(props.data, BreadcrumbLevel.TASKGROUP, revert);
  };

  const handleEditMode = () => {
    props.onEdit(props.data);
  };

  const editingDisabled = props.isReadOnly();

  return (
    <>
      {props.data && (
        <Grid container alignContent="space-between" style={{ height: '490px' }}>
          <Grid container justifyContent="flex-end" alignContent="center">
            {props.workMode !== WorkMode.EDIT ? (
              <EditButton workMode={props.workMode} handleEditMode={handleEditMode} isDisabled={editingDisabled} />
            ) : (
              <DeleteButton
                workMode={props.workMode}
                handleDeleteMode={props.onDelete}
                isDisabled={props.isReadOnly()}
              />
            )}
          </Grid>

          <Grid container justifyContent="center" alignContent="center">
            <Grid container justifyContent="center" className={sessionDetailsClasses.headerRow}>
              {props.workMode === WorkMode.VIEW && <div className={sessionDetailsClasses.headerText}>Zadanie</div>}
              {props.workMode === WorkMode.EDIT && (
                <div className={sessionDetailsClasses.headerText}>Edycja zadania</div>
              )}
              {props.workMode === WorkMode.ADD && (
                <div className={sessionDetailsClasses.headerText}>Zdefiniuj zadanie</div>
              )}
            </Grid>
            <TextField
              className={sessionDetailsClasses.formInput}
              value={taskNumber}
              onChange={(e) => {
                setTaskNumber(e.target.value.trimStart());
                setIsFieldEdited(true);
              }}
              id="outlined"
              label="Numer*"
              variant="outlined"
              disabled={props.workMode === WorkMode.VIEW || props.isSessionImported || props.data.isExamSheetConfigured}
              error={props.workMode !== WorkMode.VIEW && !isValidTaskNumber}
              inputProps={{
                style: {
                  padding: '15.5px',
                  color: editingDisabled ? 'unset' : '#2C2D3E',
                },
              }}
              InputProps={{
                startAdornment: blockIconForInput(
                  props.workMode === WorkMode.VIEW || props.isSessionImported || props.data.isExamSheetConfigured,
                ),
              }}
              InputLabelProps={{
                style: { color: '#2C2D3E' },
              }}
              FormHelperTextProps={{
                style: {
                  fontSize: '11px',
                  color: '#BC0000',
                },
              }}
              helperText={
                props.workMode !== WorkMode.VIEW && !isValidTaskNumber
                  ? 'Pole "Numer" musi być unikatowe dla arkusza'
                  : undefined
              }
            />
          </Grid>
          <Grid container justifyContent="flex-end">
            {props.workMode === WorkMode.VIEW && (
              <AddSessionItemButton
                caption="Dodaj zasady oceniania"
                disabled={props.isSessionImported || !!props.data.gradeRules}
                onClick={() => props.onAddChild(BreadcrumbLevel.GRADERULES, props.data.id, props.data.examSheetId)}
              />
            )}
            {props.workMode !== WorkMode.VIEW && (
              <ActionButtons disable={isDisabled} saveHandle={saveChanges} closeHandle={closeHandle} />
            )}
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default TaskDetails;
